import React from 'react';
import { Drawer } from 'antd';
import { COMPACT_LAYOUT_QUERY } from './useCompactWorkspaceLayout.js';

const MOBILE_TIMELINE_GAP = 18;
const CANVAS_PANEL_MIN_WIDTH = 320;
const CANVAS_PANEL_MAX_WIDTH = 980;
const CANVAS_MAIN_MIN_WIDTH = 360;

function conditionalValue(condition, whenTrue, whenFalse = null) {
  return condition ? whenTrue : whenFalse;
}

function resolveMobileCanvasDrawerContainer() {
  const mobileCanvasDrawerContainer = typeof document !== 'undefined' ? document.body : false;
  return mobileCanvasDrawerContainer;
}

function resolveCompactLayers(isCompactLayout, isNarrowViewport) {
  const shouldUseCompactLayers = isCompactLayout || isNarrowViewport;
  return shouldUseCompactLayers;
}

function resolveMobileCanvasDrawer(shouldUseCompactLayers, showCanvasPanel, canvasWorkspaceFullscreenMode) {
  const shouldShowMobileCanvasDrawer = shouldUseCompactLayers && showCanvasPanel && !canvasWorkspaceFullscreenMode;
  return shouldShowMobileCanvasDrawer;
}

function resolveCanvasResizeHandle(showCanvasPanel, isCompactLayout, canvasWorkspaceFullscreenMode) {
  const shouldShowCanvasResizeHandle = showCanvasPanel && !isCompactLayout && !canvasWorkspaceFullscreenMode;
  return shouldShowCanvasResizeHandle;
}

function resolveRecommendationPanelVisible(recommendationPanelNode, shouldRenderMobileFloatingLayers) {
  return recommendationPanelNode && shouldRenderMobileFloatingLayers;
}

function resolveComposerVisible(composerNode, shouldRenderMobileFloatingLayers) {
  return composerNode && shouldRenderMobileFloatingLayers;
}

function resolveDesktopCanvasPanel(isCompactLayout, showCanvasPanel, canvasPanelNode) {
  return !isCompactLayout && showCanvasPanel && canvasPanelNode;
}

function clampCanvasPanelWidth(width, maxWidth) {
  return Math.min(Math.max(CANVAS_PANEL_MIN_WIDTH, Math.round(width)), maxWidth);
}

/**
 * Pure layout shell for conversation pane composition.
 */
export default function ChatWorkspaceConversationLayout({
  isCompactLayout,
  showCanvasPanel,
  canvasWorkspaceFullscreenMode,
  onCanvasPanelClose,
  canvasPanelNode,
  composerNode,
  mainContentNode,
  recommendationPanelNode,
  background = '#eef3fb',
}) {
  const [isNarrowViewport, setIsNarrowViewport] = React.useState(false);
  const [canvasPanelWidth, setCanvasPanelWidth] = React.useState(null);
  const layoutRef = React.useRef(null);
  const canvasResizeMetricsRef = React.useRef(null);
  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const mediaQuery = window.matchMedia(COMPACT_LAYOUT_QUERY);
    const handleChange = (event) => setIsNarrowViewport(Boolean(event.matches));
    setIsNarrowViewport(mediaQuery.matches);
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  const shouldUseCompactLayers = resolveCompactLayers(isCompactLayout, isNarrowViewport);
  const mobileLayerVars = conditionalValue(
    shouldUseCompactLayers,
    { '--flare-mobile-timeline-bottom-space': `${MOBILE_TIMELINE_GAP}px` },
    {}
  );
  const shouldShowMobileCanvasDrawer = resolveMobileCanvasDrawer(
    shouldUseCompactLayers, showCanvasPanel, canvasWorkspaceFullscreenMode
  );
  const canCloseMobileCanvasDrawer = typeof onCanvasPanelClose === 'function';
  const shouldRenderMobileFloatingLayers = !shouldShowMobileCanvasDrawer;
  const mobileCanvasDrawerContainer = resolveMobileCanvasDrawerContainer();
  const shouldShowCanvasResizeHandle = resolveCanvasResizeHandle(
    showCanvasPanel, isCompactLayout, canvasWorkspaceFullscreenMode
  );
  const recommendationPanelVisible = resolveRecommendationPanelVisible(
    recommendationPanelNode, shouldRenderMobileFloatingLayers
  );
  const composerVisible = resolveComposerVisible(composerNode, shouldRenderMobileFloatingLayers);
  const desktopCanvasPanelVisible = resolveDesktopCanvasPanel(isCompactLayout, showCanvasPanel, canvasPanelNode);
  const canvasPanelColumn = conditionalValue(
    Boolean(canvasPanelWidth),
    `minmax(${CANVAS_PANEL_MIN_WIDTH}px, ${canvasPanelWidth}px)`,
    `minmax(${CANVAS_PANEL_MIN_WIDTH}px, min(38%, ${CANVAS_PANEL_MAX_WIDTH}px))`
  );
  const expandedGridColumns = conditionalValue(
    canvasWorkspaceFullscreenMode,
    'minmax(0, 1fr)',
    `minmax(0, 1fr) ${canvasPanelColumn}`
  );
  const gridTemplateColumns = conditionalValue(
    [showCanvasPanel, !isCompactLayout].every(Boolean),
    expandedGridColumns,
    'minmax(0, 1fr)'
  );
  const updateCanvasPanelWidth = React.useCallback((clientX) => {
    const metrics = canvasResizeMetricsRef.current;
    if (!metrics) {
      return;
    }
    const maxWidth = Math.max(
      CANVAS_PANEL_MIN_WIDTH,
      Math.min(CANVAS_PANEL_MAX_WIDTH, metrics.width - CANVAS_MAIN_MIN_WIDTH),
    );
    const nextWidth = clampCanvasPanelWidth(metrics.right - clientX, maxWidth);
    setCanvasPanelWidth(nextWidth);
  }, []);
  const handleCanvasResizePointerDown = React.useCallback((event) => {
    if (!shouldShowCanvasResizeHandle) {
      return;
    }
    const layoutRect = layoutRef.current?.getBoundingClientRect?.();
    if (!layoutRect || layoutRect.width <= 0) {
      return;
    }
    canvasResizeMetricsRef.current = {
      right: layoutRect.right,
      width: layoutRect.width,
    };
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    updateCanvasPanelWidth(event.clientX);
  }, [shouldShowCanvasResizeHandle, updateCanvasPanelWidth]);
  const handleCanvasResizePointerMove = React.useCallback((event) => {
    updateCanvasPanelWidth(event.clientX);
  }, [updateCanvasPanelWidth]);
  const handleCanvasResizePointerEnd = React.useCallback((event) => {
    canvasResizeMetricsRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  }, []);

  return (
    <div
      className={['flare-chat-conversation-layout', conditionalValue(isCompactLayout, 'is-compact', '')].filter(Boolean).join(' ')}
      ref={layoutRef}
      style={{
        background,
        display: 'grid',
        flex: 1,
        gridTemplateColumns,
        minHeight: 0,
      }}
    >
      <div
        className="flare-chat-conversation-main"
        style={{
          background,
          display: conditionalValue([canvasWorkspaceFullscreenMode, !isCompactLayout].every(Boolean), 'none', 'flex'),
          flexDirection: 'column',
          minHeight: 0,
          position: 'relative',
          ...mobileLayerVars,
        }}
      >
        {conditionalValue(!canvasWorkspaceFullscreenMode, mainContentNode)}
        {conditionalValue([!canvasWorkspaceFullscreenMode, recommendationPanelVisible].every(Boolean), (
          <div
            className={conditionalValue(shouldUseCompactLayers, 'flare-chat-mobile-recommendation', 'flare-chat-inline-recommendation')}
            style={{ background }}
          >
            {recommendationPanelNode}
          </div>
        ))}
        {conditionalValue(shouldShowMobileCanvasDrawer, (
          <Drawer
            className="flare-chat-mobile-canvas-drawer-panel"
            closable={canCloseMobileCanvasDrawer}
            getContainer={mobileCanvasDrawerContainer}
            mask
            maskClosable={canCloseMobileCanvasDrawer}
            onClose={canCloseMobileCanvasDrawer ? onCanvasPanelClose : undefined}
            open
            placement="right"
            width="100%"
            rootClassName="flare-chat-mobile-canvas-drawer-root"
            rootStyle={{
              inset: 0,
              position: 'fixed',
              zIndex: 'var(--flare-mobile-layer-canvas-drawer, 22)',
            }}
            styles={{
              body: {
                padding: 0,
              },
            }}
          >
            {canvasPanelNode}
          </Drawer>
        ))}
        {conditionalValue([!canvasWorkspaceFullscreenMode, composerVisible].every(Boolean), (
          <div
            className={conditionalValue(shouldUseCompactLayers, 'flare-chat-mobile-composer', 'flare-chat-inline-composer')}
            style={{ background }}
          >
            {composerNode}
          </div>
        ))}
      </div>
      {conditionalValue(desktopCanvasPanelVisible, (
        <div
          className="flare-chat-canvas-panel-host"
          style={{
            minHeight: 0,
            minWidth: 0,
            position: 'relative',
          }}
        >
          {conditionalValue(shouldShowCanvasResizeHandle, (
            <div
              aria-label="调整画布宽度"
              aria-orientation="vertical"
              className="flare-chat-canvas-resize-handle"
              onPointerCancel={handleCanvasResizePointerEnd}
              onPointerDown={handleCanvasResizePointerDown}
              onPointerMove={handleCanvasResizePointerMove}
              onPointerUp={handleCanvasResizePointerEnd}
              role="separator"
              style={{
                bottom: 0,
                cursor: 'col-resize',
                left: -6,
                position: 'absolute',
                top: 0,
                touchAction: 'none',
                userSelect: 'none',
                width: 12,
                zIndex: 20,
              }}
            />
          ))}
          {canvasPanelNode}
        </div>
      ))}
    </div>
  );
}
